import React, { useEffect, useState, useMemo, useContext } from "react";
import { FUEL_SURCHARGE_TYPE } from "api/FuelSurcharge";
import { CHARGE_TYPE } from "api/Charges";
import { ChargeAddModal, ChargeRemoveModal, TextLink } from "components/ChargeDialogs";
import { Input, Select, Textarea } from "theme-ui";
import ProjectNextSteps from "components/ProjectNextSteps";
import ProjectClientInputs from "components/ProjectClientInputs";
import { useNavigate } from "react-router-dom";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import { getProjectNextStatus, updateProject, getProjectQuotes, getProjectTimeline, createDispatch, getDispatchByPurchaseOrder, markDispatchOnTheWay, updateDispatch } from "api/Projects";
import BasicButton from "components/forms/BasicButton";
import {
  QuestionMarkIcon,
  CheckMarkIcon,
  ClientsIcon,
  FinancialIcon,
  RFQIcon,
  QuotesIcon,
  LongArrowIcon,
  InvoicesIcon,
  PlusCircleIcon,
  PurchaseOrderIcon,
  PlusIcon,
  ProvidersIcon,
  PencilIcon,
} from "components/svg";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import Checkbox from "components/forms/Checkbox";
import { useToast } from "components/toast";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import Span from "components/typography/Span";
import ReloadIcon from "components/svg/icons/ReloadIcon";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import MultiText from "components/forms/MultiText";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import InvoiceStatusBadge from "components/ui/InvoiceStatusBadge";
import Card from "components/figmaUI/Card";
import PaymentTracker, { PaymentStage } from "components/figmaUI/PaymentTracker";
import { getInvoicesForPO } from "api/ProviderInvoice";
// Session 27 — SiteMapSection import removed with the site-map card;
// sidebar SiteMapSection in Projects/view.tsx is the canonical preview.

type Payment = {
  updatedAt: string;
};

const QUALIFYING_PO_STATUSES = new Set([2, 3, 5]);

/**
 * What the Items and Timeline sections show when there is no timeline to
 * draw. Only "loading" spins; a failed request says so and offers a retry,
 * and an empty project says it is empty.
 */
const TimelineFallback = ({ status, onRetry }: { status: "loading" | "ready" | "error"; onRetry: () => void }) => {
  if (status === "loading") {
    return (
      <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
        <Spinner size={25} strokeWidth={3} title="loading" />
      </Box>
    );
  }
  if (status === "error") {
    return (
      <Box sx={{ textAlign: "center", paddingTop: "30px", fontFamily: "Poppins, sans-serif", fontSize: "13px", color: themevals.colors.dkgrey }}>
        Could not load this project&apos;s records.{" "}
        <button
          type="button"
          onClick={onRetry}
          style={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", background: "none", border: "none", padding: 0, color: themevals.colors.base_800, textDecoration: "underline", cursor: "pointer" }}
        >
          Try again
        </button>
      </Box>
    );
  }
  return (
    <Box sx={{ textAlign: "center", paddingTop: "30px", fontFamily: "Poppins, sans-serif", fontSize: "13px", color: themevals.colors.dkgrey }}>
      No quotes or orders on this project yet.
    </Box>
  );
};

const Overview = ({
  isCompany,
  isProvider,
  project,
  reloadProject,
  providerId,
}: {
  isCompany: boolean;
  isProvider: boolean;
  project: any;
  reloadProject: (project: any) => void;
  providerId?: string;
}) => {
  const [nextStep, setNextStep] = useState<any>(null);
  const [confirmStepModalOpen, setConfirmStepModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dispatchesByPo, setDispatchesByPo] = useState<Record<string, any[]>>({});
  const [dispatchSubmitting, setDispatchSubmitting] = useState(false);
  const [createDispatchOpen, setCreateDispatchOpen] = useState(false);
  const [editDispatchOpen, setEditDispatchOpen] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<any>(null);
  const [selectedDispatch, setSelectedDispatch] = useState<any>(null);
  const [selectedStep, setSelectedStep] = useState<any>(null);
  const [projectTimeline, setProjectTimeline] = useState<any>(null);
  /**
   * Whether the timeline request has ANSWERED, separately from what it
   * answered. The page used to spin forever in two cases a refresh
   * "fixed": the request failed (the API helper returns the error object,
   * which has no length), or it succeeded with an empty list. Both drew
   * the same spinner as "still loading", with nothing left in flight.
   */
  const [timelineStatus, setTimelineStatus] = useState<"loading" | "ready" | "error">("loading");
  const [pendingInvoicesByPo, setPendingInvoicesByPo] = useState<{ poId: string; poSequence: number; invoices: any[] }[]>([]);
  const [invoiceStatusByPo, setInvoiceStatusByPo] = useState<Record<string, { status: string; count: number; invoice?: any }>>({});
  const [unsentPOs, setUnsentPOs] = useState<{ id: string; sequence: number; providerName: string }[]>([]);
  // Ad-hoc charges (AD_HOC_CHARGES_SPEC.md): the add modal is opened for one
  // invoice; the live PO(s) on the job are offered as the provider side.
  const [chargeModal, setChargeModal] = useState<{ invoice: any; pos: any[] } | null>(null);
  const [removeChargeTarget, setRemoveChargeTarget] = useState<{ invoice: any; chargeId: string; name: string } | null>(null);
  const openChargeModal = (invoice: any, pos: any[]) => setChargeModal({ invoice, pos: pos ?? [] });

  const navigate = useNavigate();
  const toast = useToast();
  const columnHelper = createColumnHelper();
  const { user } = useContext<any>(UserContext);

  // Keyed on the project id, not run once on mount: if the view hands this
  // component a project before its id is there, a mount-only effect
  // fetched `/projects/undefined/timeline` and never tried again.
  useEffect(() => {
    if (!project?.id) return;
    _init();
  }, [project?.id]);

  /** One retry after a short pause - covers a cold Lambda or a dropped request. */
  const _fetchTimeline = async (): Promise<any[] | null> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const res = await getProjectTimeline({ data: { id: project?.id } });
      if (Array.isArray(res)) return res;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 1500));
    }
    return null;
  };

  const _init = async () => {
    setTimelineStatus("loading");
    // In parallel: the timeline no longer waits behind next-status, and a
    // project without a status no longer throws before the timeline is
    // even requested.
    const [res, timelineRes] = await Promise.all([
      project?.status?.id ? getProjectNextStatus({ data: { id: project.status.id } }) : Promise.resolve(null),
      _fetchTimeline(),
    ]);
    setNextStep(res);

    if (timelineRes === null) {
      setProjectTimeline(null);
      setTimelineStatus("error");
      return;
    }
    setProjectTimeline(timelineRes);
    setTimelineStatus("ready");

    // Collect unsent POs (status 0 = New) from the timeline (company/admin users only)
    if (isCompany && Array.isArray(timelineRes)) {
      const unsent: { id: string; sequence: number; providerName: string }[] = [];
      for (const entry of timelineRes) {
        const pos = entry?.purchaseOrders || [];
        for (const po of pos) {
          if (Number(po.status) === 0) {
            unsent.push({
              id: po.id,
              sequence: po.sequence,
              providerName: po.provider?.name || "Unknown",
            });
          }
        }
      }
      setUnsentPOs(unsent);
    }

    // Fetch provider invoices for all POs in the timeline (company users only).
    // Was `!isProvider`, so clients called a staff/provider-only route and got
    // the app's 403 screen (2026-10-20).
    if (isCompany && Array.isArray(timelineRes)) {
      const allPOs: any[] = timelineRes.flatMap((entry: any) => entry?.purchaseOrders || []);
      if (allPOs.length > 0) {
        try {
          const pendingResults: any[] = [];
          const statusMap: Record<string, { status: string; count: number; invoice?: any }> = {};

          await Promise.all(
            allPOs.map(async (po: any) => {
              const res = await getInvoicesForPO(po.id);
              if (res.success && Array.isArray(res.data) && res.data.length > 0) {
                const pending = res.data.filter(
                  (inv: any) => inv.status === "pending_approval" || inv.status === "review_needed" || inv.status === "revision_pending"
                );
                if (pending.length > 0) {
                  pendingResults.push({ poId: po.id, poSequence: po.sequence, invoices: pending });
                  statusMap[po.id] = { status: "pending", count: pending.length, invoice: pending[0] };
                } else {
                  const approved = res.data.find((inv: any) => inv.status === "bill_created");
                  const rejected = res.data.find((inv: any) => inv.status === "rejected");
                  if (approved) {
                    statusMap[po.id] = { status: "approved", count: 1, invoice: approved };
                  } else if (rejected) {
                    statusMap[po.id] = { status: "rejected", count: 1, invoice: rejected };
                  }
                }
              }
            })
          );
          setPendingInvoicesByPo(pendingResults);
          setInvoiceStatusByPo(statusMap);
        } catch (e) {
          // Non-critical
        }
      }
    }
  };

  const _transitionToNextStatus = async (transition: any) => {
    if (transition.actionType === "REDIRECT") {
      let url = transition.actionUrl;
      if (url?.includes(":id")) {
        url = url.replace(":id", project?.id);
      }
      if (url?.includes(":quoteId") && projectTimeline?.[0]?.quote?.id) {
        url = url.replace(":quoteId", projectTimeline[0].quote.id);
      }
      navigate(url);
    } else if (transition.actionType === "TRANSITION") {
      setSubmitting(true);
      const res = await updateProject({ data: { id: project?.id }, update: { status: { id: transition.toStatusId } } });
      reloadProject(res);
      setSubmitting(false);
      setConfirmStepModalOpen(false);
      setSelectedStep(null);
      toast.add(`Project status set to ${res.status.name}.`, "var(--theme-ui-colors-success)");
      const stepRes = await getProjectNextStatus({ data: { id: res?.status.id } });
      setNextStep(stepRes);
    }
  };

  const cancelStepModal = () => {
    setConfirmStepModalOpen(false);
  };

  const confirmStepAction = async (step: any) => {
    if (step.transition.actionType === "TRANSITION") {
      setConfirmStepModalOpen(true);
      setSelectedStep(step);
    } else {
      _transitionToNextStatus(step.transition);
    }
  };

  const submitStepAction = async (step: any) => {
    _transitionToNextStatus(step.transition);
  };

  const getQuoteCostTotal = (lineitems: any[]) => {
    if (!lineitems || !Array.isArray(lineitems)) return 0;
    return lineitems.reduce((sum, item) => {
      const itemCost = (item.cost || 0) * (item.quantity || 0);
      return sum + itemCost;
    }, 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  };

  const getQuotePriceTotal = (lineitems: any[]) => {
    if (!lineitems || !Array.isArray(lineitems)) return 0;
    return lineitems.reduce((sum, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 0);
      return sum + itemTotal;
    }, 0);
  };

  const providerPurchaseOrders = useMemo(() => {
    if (!isProvider || !providerId || !Array.isArray(projectTimeline)) {
      return [];
    }

    const orders: any[] = [];
    for (const entry of projectTimeline) {
      const po = entry?.purchaseOrders?.[0];
      if (!po) continue;
      if (po?.provider?.id !== providerId) continue;
      if (!QUALIFYING_PO_STATUSES.has(po.status)) continue;
      orders.push(po);
    }

    return orders;
  }, [isProvider, providerId, projectTimeline]);

  useEffect(() => {
    const loadDispatches = async () => {
      if (!isProvider || !providerId) {
        return;
      }
      if (!Array.isArray(providerPurchaseOrders) || providerPurchaseOrders.length === 0) {
        setDispatchesByPo({});
        return;
      }

      try {
        const results = await Promise.all(
          providerPurchaseOrders.map(async (po: any) => {
            try {
              const res = await getDispatchByPurchaseOrder({ reference: po.id });
              if (Array.isArray(res)) return [po.id, res];
              if (res && res.id) return [po.id, [res]];
            } catch (error) {
              console.error("Failed to load dispatches", error);
            }
            return [po.id, []];
          })
        );

        setDispatchesByPo(Object.fromEntries(results));
      } catch (error) {
        console.error("Failed to load dispatches", error);
      }
    };

    loadDispatches();
  }, [isProvider, providerId, providerPurchaseOrders]);

  const refreshDispatchesForPo = async (poId: string) => {
    if (!poId) return;
    try {
      const res = await getDispatchByPurchaseOrder({ reference: poId });
      setDispatchesByPo((prev) => ({
        ...prev,
        [poId]: Array.isArray(res) ? res : res && res.id ? [res] : [],
      }));
    } catch (error) {
      console.error("Failed to refresh dispatch list", error);
    }
  };

  const openAssetTab = (assetType: string, id: string) => {
    if (assetType === "quote") {
      navigate(`/quotes/${id}`);
    } else if (assetType === "invoice") {
      navigate(`/invoices/${id}`);
    } else if (assetType === "rfq") {
      navigate(`/quote-requests/${id}`);
    } else if (assetType === "po") {
      navigate(`/purchase-orders/${id}`);
    }
  };

  const openCreateDispatchModal = (purchaseOrder: any) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setSelectedDispatch(null);
    setCreateDispatchOpen(true);
  };

  const closeCreateDispatchModal = () => {
    setCreateDispatchOpen(false);
    setSelectedDispatch(null);
    setSelectedPurchaseOrder(null);
  };

  const openEditDispatchModal = (purchaseOrder: any, dispatch: any) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setSelectedDispatch(dispatch);
    setEditDispatchOpen(true);
  };

  const closeEditDispatchModal = () => {
    setEditDispatchOpen(false);
    setSelectedDispatch(null);
    setSelectedPurchaseOrder(null);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "id",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Name</span>,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const quantity = info.getValue();
        const unit = info.row.original?.unit;
        return quantity !== undefined ? `${quantity} ${unit || ""}`.trim() : "";
      },
      header: () => <span>Quantity</span>,
    }),
    ...(isCompany
      ? [
          columnHelper.accessor((row: any) => row?.markup, {
            id: "markup",
            cell: (info: any) => `${info.getValue()}%`,
            header: () => <span>Markup</span>,
          }),
        ]
      : []),
    ...(isCompany || isProvider
      ? [
          columnHelper.accessor((row: any) => row?.cost, {
            id: "baseCost",
            cell: (info: any) => `$${info.getValue()}`,
            header: () => <span>Cost Per Unit</span>,
          }),
        ]
      : []),
    ...(isCompany || !isProvider
      ? [
          columnHelper.accessor((row: any) => row?.price, {
            id: "price",
            cell: (info: any) => `$${info.getValue()}`,
            header: () => <span>Price Per Unit</span>,
          }),
        ]
      : []),
    ...(isCompany || isProvider
      ? [
          columnHelper.accessor((row: any) => row?.cost * row?.quantity, {
            id: "totalCost",
            cell: (info: any) => formatCurrency(info.getValue()),
            header: () => <span>Total Cost</span>,
          }),
        ]
      : []),
    ...(isCompany || !isProvider
      ? [
          columnHelper.accessor((row: any) => row?.price * row?.quantity, {
            id: "totalPrice",
            cell: (info: any) => formatCurrency(info.getValue()),
            header: () => <span>Total Price</span>,
          }),
        ]
      : []),
  ];

  const getPoDisplayDate = (po: any) => {
    switch (po?.status) {
      case 0:
        return po.createdAt ? moment(po.createdAt).format("MMM D, YYYY") : "-";
      case 2:
        return po.acceptedDate ? moment(po.acceptedDate).format("MMM D, YYYY") : "-";
      case 3:
        return po.dueDate ? moment(po.dueDate).format("MMM D, YYYY") : "-";
      case 4:
        return po.deniedDate ? moment(po.deniedDate).format("MMM D, YYYY") : "-";
      default:
        return po.createdAt ? moment(po.createdAt).format("MMM D, YYYY") : "-";
    }
  };

  const markOnTheWay = async (dispatchId: string, purchaseOrderId: string) => {
    if (!dispatchId) return;
    setDispatchSubmitting(true);
    try {
      const res = await markDispatchOnTheWay({ id: dispatchId });
      if (res?.id) {
        toast.add("Dispatch marked on the way.", "var(--theme-ui-colors-success)");
        void trackActivity({
          module: "projects",
          action: "mark_dispatch_on_the_way",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || project?.company?.id || null,
          entityType: "dispatch",
          entityId: res.id,
          metadata: {
            projectId: project?.id || null,
            purchaseOrderId,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        await refreshDispatchesForPo(purchaseOrderId);
      } else {
        toast.add(res?.message || "Unable to mark dispatch on the way.", "var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message || "Unable to mark dispatch on the way.", "var(--theme-ui-colors-danger)");
    } finally {
      setDispatchSubmitting(false);
    }
  };

  const copyDispatchLink = (dispatchId: string, purchaseOrderId: string) => {
    if (!dispatchId) return;
    const link = `${window.location.origin}/dispatches/${dispatchId}`;

    void trackActivity({
      module: "projects",
      action: "copy_dispatch_link",
      actionType: "click",
      userId: user?.id || null,
      companyId: user?.company?.id || project?.company?.id || null,
      entityType: "dispatch",
      entityId: dispatchId,
      metadata: {
        projectId: project?.id || null,
        purchaseOrderId,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          toast.add("Dispatch link copied.", "var(--theme-ui-colors-success)");
        })
        .catch(() => {
          toast.add("Unable to copy dispatch link.", "var(--theme-ui-colors-danger)");
        });
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = link;
      document.body.appendChild(tempInput);
      tempInput.select();
      tempInput.setSelectionRange(0, 99999);
      try {
        document.execCommand("copy");
        toast.add("Dispatch link copied.", "var(--theme-ui-colors-success)");
      } catch (error) {
        toast.add("Unable to copy dispatch link.", "var(--theme-ui-colors-danger)");
      }
      document.body.removeChild(tempInput);
    }
  };

  const submitDispatchCreate = async (values: any) => {
    if (!selectedPurchaseOrder?.id) {
      toast.add("Unable to determine purchase order for dispatch.", "var(--theme-ui-colors-danger)");
      return;
    }

    setDispatchSubmitting(true);

    const data = {
      dispatchDate: values.dispatchDate ? moment.unix(values.dispatchDate).format("YYYY-MM-DD HH:mm:ss") : null,
      projectId: values.projectId,
      purchaseOrderId: selectedPurchaseOrder.id,
      truckIds: Array.isArray(values.truckIds) ? values.truckIds : [],
    };

    try {
      const res = await createDispatch({ data });

      if (res?.id) {
        toast.add("Dispatch created successfully.", "var(--theme-ui-colors-success)");
        void trackActivity({
          module: "projects",
          action: "create_dispatch",
          actionType: "create",
          userId: user?.id || null,
          companyId: user?.company?.id || project?.company?.id || null,
          entityType: "dispatch",
          entityId: res.id,
          metadata: {
            projectId: project?.id || null,
            purchaseOrderId: selectedPurchaseOrder.id,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        await refreshDispatchesForPo(selectedPurchaseOrder.id);
        closeCreateDispatchModal();
      } else {
        toast.add(res?.message || "An error occurred while creating the dispatch.", "var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message || "An error occurred while creating the dispatch.", "var(--theme-ui-colors-danger)");
    } finally {
      setDispatchSubmitting(false);
    }
  };

  const submitDispatchEdit = async (values: any) => {
    if (!selectedDispatch?.id || !selectedPurchaseOrder?.id) {
      toast.add("Unable to determine dispatch for update.", "var(--theme-ui-colors-danger)");
      return;
    }

    setDispatchSubmitting(true);

    const changedFields = (() => {
      const fields: string[] = [];

      const normalizeArray = (arr: any[]) =>
        (Array.isArray(arr) ? arr : [])
          .map((val) => (val === null || val === undefined ? "" : String(val).trim()))
          .filter((val) => val.length > 0)
          .sort();

      const previousTruckIds = normalizeArray(selectedDispatch?.truckIds);
      const nextTruckIds = normalizeArray(values?.truckIds);

      if (JSON.stringify(previousTruckIds) !== JSON.stringify(nextTruckIds)) {
        fields.push("truckIds");
      }

      const previousOnHold = selectedDispatch ? selectedDispatch.status === 3 : false;
      const nextOnHold = Boolean(values?.placeOnHold);
      if (selectedDispatch?.status === 1 && previousOnHold !== nextOnHold) {
        fields.push("placeOnHold");
      }

      const previousDispatchDate = selectedDispatch?.dispatchDate
        ? moment(selectedDispatch.dispatchDate).unix()
        : null;
      const nextDispatchDate =
        typeof values?.dispatchDate === "number" ? values.dispatchDate : values?.dispatchDate ?? null;
      if (
        previousDispatchDate !== nextDispatchDate &&
        (previousDispatchDate !== null || nextDispatchDate !== null)
      ) {
        fields.push("dispatchDate");
      }

      return fields;
    })();

    const nextStatus = (() => {
      if (!selectedDispatch) return undefined;
      if (selectedDispatch.status === 1) {
        return values.placeOnHold ? 3 : 1;
      }
      return selectedDispatch.status;
    })();

    const isHoldToggleApplicable = !!selectedDispatch && selectedDispatch.status === 1;
    const shouldPlaceOnHold = isHoldToggleApplicable && !!values.placeOnHold;

    const data: Record<string, any> = {
      projectId: project?.id,
      purchaseOrderId: selectedPurchaseOrder.id,
      truckIds: Array.isArray(values.truckIds) ? values.truckIds : [],
      ...(nextStatus !== undefined ? { status: nextStatus } : {}),
    };

    if (isHoldToggleApplicable) {
      if (shouldPlaceOnHold) {
        data.dispatchDate = null;
      }
    } else if (values.dispatchDate) {
      data.dispatchDate = moment.unix(values.dispatchDate).format("YYYY-MM-DD HH:mm:ss");
    }

    try {
      const res = await updateDispatch({ id: selectedDispatch.id, data });
      if (res?.id) {
        toast.add("Dispatch updated successfully.", "var(--theme-ui-colors-success)");
        if (changedFields.length > 0) {
          void trackActivity({
            module: "projects",
            action: "update_dispatch",
            actionType: "update",
            userId: user?.id || null,
            companyId: user?.company?.id || project?.company?.id || null,
            entityType: "dispatch",
            entityId: res.id,
            metadata: {
              projectId: project?.id || null,
              purchaseOrderId: selectedPurchaseOrder.id,
              changedFields,
            },
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          });
        }
        await refreshDispatchesForPo(selectedPurchaseOrder.id);
        closeEditDispatchModal();
      } else {
        toast.add(res?.message || "An error occurred while updating the dispatch.", "var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message || "An error occurred while updating the dispatch.", "var(--theme-ui-colors-danger)");
    } finally {
      setDispatchSubmitting(false);
    }
  };

  const dispatchInitialValues = useMemo(
    () => ({
      dispatchDate: selectedDispatch?.dispatchDate ? moment(selectedDispatch.dispatchDate).unix() : null,
      truckIds: Array.isArray(selectedDispatch?.truckIds) ? selectedDispatch.truckIds : [],
      purchaseOrderId: selectedPurchaseOrder?.id ?? null,
      projectId: project?.id,
      placeOnHold: selectedDispatch ? selectedDispatch.status === 3 : false,
    }),
    [selectedDispatch, selectedPurchaseOrder?.id, project?.id]
  );

  // Derive estimated cost: prefer PO costs; fall back to invoice/quote costs
  const estimatedCostFromTimeline = useMemo(() => {
    if (!Array.isArray(projectTimeline) || projectTimeline.length === 0) {
      return Number(project?.estimatedCost || 0);
    }
    const sumItems = (items: any[]) => (items || []).reduce((sum, it) => sum + Number(it?.cost || 0) * Number(it?.quantity || 0), 0);
    const total = projectTimeline.reduce((acc: number, entry: any) => {
      // Same rule as the backend's projectTotal, so cost and total can't
      // disagree about which paths count: a superseded quote (5) or a
      // voided invoice (4) means the chain was replaced.
      if (entry?.quote?.status === 5 || entry?.invoice?.status === 4) return acc;
      // A CANCELLED PO (6) costs nothing. Re-issuing a PO cancels the
      // previous one, and without this the replaced PO's cost was still
      // counted — double-costing every job that had its PO reissued.
      const livePos = Array.isArray(entry?.purchaseOrders)
        ? entry.purchaseOrders.filter((po: any) => po?.status !== 6)
        : [];
      const poItems = livePos.flatMap((po: any) => (Array.isArray(po?.lineitems) ? po.lineitems : []));
      if (poItems.length > 0) return acc + sumItems(poItems);
      const invItems = Array.isArray(entry?.invoice?.lineitems) ? entry.invoice.lineitems : [];
      if (invItems.length > 0) return acc + sumItems(invItems);
      const quoteItems = Array.isArray(entry?.quote?.lineitems) ? entry.quote.lineitems : [];
      return acc + sumItems(quoteItems);
    }, 0);
    return total;
  }, [projectTimeline, project?.estimatedCost]);

  const hasDiscount = typeof project.discountAmount === "number" && project.discountAmount > 0;
  const discountedTotal = hasDiscount ? Number(project.projectTotal || 0) - Number(project.discountAmount || 0) : Number(project.projectTotal || 0);

  const discountedMargin = discountedTotal > 0 ? Math.round(((discountedTotal - Number(estimatedCostFromTimeline || 0)) / discountedTotal) * 100) : 0;

  // Show payment tracker when project status is Complete, Closed, or Invoice Sent (matching PO overview logic)
  const showPaymentTracker = ['Complete', 'Closed', 'Invoice Sent'].includes(project?.status?.name);

  // Get all POs for the logged-in provider from the project timeline
  const providerPurchaseOrdersForTracker = useMemo(() => {
    if (!isProvider || !providerId || !Array.isArray(projectTimeline)) {
      return [];
    }

    const orders: any[] = [];
    for (const entry of projectTimeline) {
      const pos = entry?.purchaseOrders || [];
      for (const po of pos) {
        // Include PO if it belongs to this provider
        if (po?.provider?.id === providerId) {
          orders.push(po);
        }
      }
    }

    return orders;
  }, [isProvider, providerId, projectTimeline]);

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Overview
      </H5>
      {/* Client's open items in one card (2026-10-12): early pay, PO / site maps, date needed by. */}
      {!isCompany && !isProvider && project?.id && <ProjectNextSteps project={project} />}
      {/* Staff: what the client added (date needed by, PO, labeled files). */}
      {isCompany && project?.id && <ProjectClientInputs project={project} />}
      {showPaymentTracker && isProvider && providerPurchaseOrdersForTracker.length > 0 && (
        <>
          <Flex sx={{ marginTop: "30px" }}>
            <Box>
              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" fontWeight="600" color={themevals.colors.dkgrey}>
                Payment Tracker
              </P>
            </Box>
          </Flex>
          {providerPurchaseOrdersForTracker.map((po: any) => {
            const poNumber = po?.sequence
              ? `PO# GMO-${String(po.sequence).padStart(5, '0')}`
              : 'PO# GMO-00000';

            // Find the invoice associated with this PO from the project timeline
            const associatedInvoice = projectTimeline?.find((entry: any) => 
              entry?.purchaseOrders?.some((entryPo: any) => entryPo?.id === po?.id)
            )?.invoice;

            // Check if invoice has been sent (status >= 1 and has sentDate)
            const invoiceSent = associatedInvoice?.status >= 1 && associatedInvoice?.sentDate;
            const invoiceSentDate = invoiceSent ? moment(associatedInvoice.sentDate).format("MMM D, YYYY") : undefined;

            // Check if client has paid (invoice has a deposited payment)
            const depositedPayment = associatedInvoice?.payments?.find((p: any) => p.deposited === true);
            const clientPaid = !!depositedPayment;
            const clientPaidDate = depositedPayment 
              ? moment(depositedPayment.depositedDate || depositedPayment.transactionDate).format("MMM D, YYYY")
              : undefined;

            // Check if PO payment status is marked as Paid (paymentStatus === 2)
            const poPaid = po?.paymentStatus === 2;
            
            // Get the paid date from the PO payments (matching PO Overview logic)
            const poPaidDate = (() => {
              if (poPaid) {
                const payments = po?.payments || [];
                const paidPayment = payments.find((p: any) => p.transactionDate);
                if (paidPayment?.transactionDate) {
                  return moment(paidPayment.transactionDate).format("MMM D, YYYY");
                }
              }
              return undefined;
            })();

            // Check if client payment is late (invoice sent but client hasn't paid and past due date)
            const isClientPaymentLate = (() => {
              // Only check if invoice sent but client hasn't paid yet
              if (!invoiceSent || clientPaid || poPaid) {
                return false;
              }
              
              // Check if we have a due date on the invoice
              if (associatedInvoice?.dueDate) {
                const dueDate = moment(associatedInvoice.dueDate);
                const today = moment();
                return today.isAfter(dueDate);
              }
              
              return false;
            })();

            // Placeholder payment stages (3 stages as per ticket requirement)
            const paymentStages: PaymentStage[] = [
              {
                label: 'Client Invoiced',
                date: invoiceSentDate,
                isCompleted: invoiceSent,
                isActive: invoiceSent,
              },
              {
                label: 'Client Paid',
                date: clientPaidDate || (invoiceSentDate 
                  ? `Approx. ${moment(associatedInvoice.sentDate).add(project?.client?.terms || 30, 'days').format("MMM D, YYYY")}`
                  : `Approx. ${project?.client?.terms || 30} days after client is invoiced`),
                isCompleted: clientPaid,
                isActive: clientPaid && !isClientPaymentLate,
                isLate: isClientPaymentLate,
              },
              {
                label: 'Paid',
                date: poPaidDate,
                isCompleted: poPaid,
                isActive: poPaid,
              },
            ];

            return (
              <Card
                key={po.id}
                showHeader={true}
                headerLeft={
                  <Box sx={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 600 }}>
                    {poNumber}
                  </Box>
                }
                $customStyles={{ marginTop: '20px', marginBottom: '20px', height: 'auto' }}
              >
                <PaymentTracker stages={paymentStages} />
              </Card>
            );
          })}
        </>
      )}
      {((nextStep && nextStep.length > 0) || pendingInvoicesByPo.length > 0 || unsentPOs.length > 0) && isCompany && (
        <Flex sx={{ padding: "20px", background: "#DDEDFF", borderRadius: "6px", flexDirection: "row" }}>
          <Flex sx={{ flexDirection: "column", flex: 1 }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "#104E9A" }}>Next Steps</Box>
            {nextStep && Array.isArray(nextStep) && nextStep.length > 0 && (
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#104E9A", mt: 1 }}>
                {nextStep.length === 1
                  ? nextStep[0].transition.description
                  : `${nextStep[0].transition.description.replace(/\.$/, "")} or ${nextStep[1].transition.description.charAt(0).toLowerCase()}${nextStep[1].transition.description.slice(1)}`}
              </Box>
            )}
            {pendingInvoicesByPo.length > 0 && (
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#856404", mt: 1 }}>
                {(() => {
                  const totalPending = pendingInvoicesByPo.reduce((sum, p) => sum + p.invoices.length, 0);
                  const poCount = pendingInvoicesByPo.length;
                  if (totalPending === 1) {
                    const po = pendingInvoicesByPo[0];
                    const inv = po.invoices[0];
                    const poNum = `GMO-${String(po.poSequence).padStart(5, "0")}`;
                    const hasVariance = Math.abs(Number(inv.variancePct || 0)) > 5;
                    return `Provider invoice on ${poNum}${hasVariance ? " (has variance)" : ""} needs review.`;
                  }
                  return `${totalPending} provider invoice${totalPending > 1 ? "s" : ""} across ${poCount} PO${poCount > 1 ? "s" : ""} need review.`;
                })()}
              </Box>
            )}
            {isCompany && unsentPOs.length > 0 && (
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#856404", mt: 1 }}>
                {unsentPOs.length === 1
                  ? `PO GMO-${String(unsentPOs[0].sequence).padStart(5, "0")} (${unsentPOs[0].providerName}) has not been sent to the provider yet.`
                  : `${unsentPOs.length} purchase orders have not been sent to providers yet.`}
              </Box>
            )}
          </Flex>
          <Flex sx={{ flex: 1, justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
            {nextStep &&
              Array.isArray(nextStep) &&
              nextStep.length > 0 &&
              nextStep.map((step: any, index: number) => (
                <BasicButton
                  scheme="infoOutline"
                  styles={{ margin: ".25rem 0.625rem .25rem 0" }}
                  type="button"
                  onClick={() => confirmStepAction(step)}
                  $iconHeight="14px"
                  $iconWidth="14px"
                  $loadingColor={themevals.colors.danger}
                  keepText={false}
                  key={index}
                >
                  {step.transition.actionText}
                </BasicButton>
              ))}
            {pendingInvoicesByPo.length > 0 && (
              <BasicButton
                scheme="warningOutline"
                styles={{ margin: ".25rem 0 .25rem 0", backgroundColor: "#FFF3CD" }}
                type="button"
                onClick={() => {
                  const firstPo = pendingInvoicesByPo[0];
                  navigate(`/purchase-orders/${firstPo.poId}?tab=invoices`);
                }}
                $iconHeight="14px"
                $iconWidth="14px"
                keepText={false}
              >
                Review Invoice{pendingInvoicesByPo.reduce((sum, p) => sum + p.invoices.length, 0) > 1 ? "s" : ""}
              </BasicButton>
            )}
            {isCompany && unsentPOs.length > 0 && (
              <BasicButton
                scheme="warningOutline"
                styles={{ margin: ".25rem 0 .25rem 0", backgroundColor: "#FFF3CD" }}
                type="button"
                onClick={() => {
                  navigate(`/purchase-orders/${unsentPOs[0].id}`);
                }}
                $iconHeight="14px"
                $iconWidth="14px"
                keepText={false}
              >
                Send PO{unsentPOs.length > 1 ? "s" : ""}
              </BasicButton>
            )}
          </Flex>
        </Flex>
      )}
      {isCompany && (
        <Flex sx={{ marginTop: "30px", gap: "20px", flexDirection: ["column", "row"] }}>
          <Flex sx={{ flex: 1, borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
            <Flex sx={{ padding: "20px", gap: "8px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, flexDirection: "row", alignItems: "center" }}>
              <Flex>
                <ClientsIcon width="18px" fill={themevals.colors.dkgrey} />
              </Flex>
              <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>Client Info</Flex>
            </Flex>
            <Grid gap={0} columns={[1, "1fr 1fr"]} sx={{ padding: "20px" }}>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Client</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {project.client?.name}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Primary Contact Name</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {project.primaryClient?.name}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Primary Contact Email</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {project.primaryClient?.contactEmail}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Primary Contact Phone</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {project.primaryClient?.phone ? project.primaryClient?.phone : "-"}
                </P>
              </Flex>
            </Grid>
          </Flex>

          {/* begin financial info */}
          <Flex sx={{ flex: 1, borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
            <Flex sx={{ padding: "20px", gap: "8px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, flexDirection: "row", alignItems: "center" }}>
              <Flex>
                <FinancialIcon width="18px" fill={themevals.colors.dkgrey} />
              </Flex>
              <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>Financial Info</Flex>
            </Flex>
            <Grid gap={0} columns={[1, "1fr 1fr"]} sx={{ padding: "20px" }}>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Estimated Project Cost</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {estimatedCostFromTimeline > 0 ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(estimatedCostFromTimeline) : "-"}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project Total</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {discountedTotal ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(discountedTotal) : "-"}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Estimated Project Margin</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {discountedMargin}%
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Discount Applied</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.success} fontSize={"14px"} fontWeight={600} marginTop={"5px"} marginBottom={"0px"}>
                  {project.discountAmount ? `-${formatCurrency(project.discountAmount)}` : "-"}
                </P>
              </Flex>
            </Grid>
            {/* Fuel surcharge on this job's client invoice(s) (2026-10-08).
                A view plus a link: the change itself happens on the invoice
                (reason, PO option, Zoho sync, refused once paid), so there is
                one way to do it. */}
            {(() => {
              const rows = (projectTimeline ?? [])
                .map((e: any) => e?.invoice)
                .filter((inv: any) => inv?.id && Number(inv.status) !== 4);
              if (!rows.length) return null;
              return (
                <Box sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "16px 20px 20px 20px" }}>
                  <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Fuel Surcharge</Label>
                  {rows.map((inv: any) => {
                    const fuel = (inv.lineitems ?? [])
                      .filter((li: any) => li?.type === FUEL_SURCHARGE_TYPE)
                      .reduce((sum: number, li: any) => sum + Number(li.price ?? 0) * Number(li.quantity ?? 0), 0);
                    const hasLine = (inv.lineitems ?? []).some((li: any) => li?.type === FUEL_SURCHARGE_TYPE);
                    const paid = (inv.payments ?? []).length > 0 || [2, 3].includes(Number(inv.status));
                    const invNo = `GMI-${String(inv.sequence ?? "").padStart(5, "0")}`;
                    const state = hasLine
                      ? fuel > 0
                        ? `${formatCurrency(fuel)} charged`
                        : "Waived for this client ($0.00)"
                      : inv.fuelSurchargeOff
                        ? "Removed (reason in Notes)"
                        : "Not on this invoice";
                    const action = paid ? null : hasLine && fuel > 0 ? "remove" : !hasLine ? "add" : null;
                    return (
                      <Flex key={inv.id} sx={{ alignItems: "baseline", justifyContent: "space-between", gap: "12px", marginTop: "5px", flexWrap: "wrap" }}>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"0px"} marginBottom={"0px"}>
                          {state}
                          <Span fontFamily="Poppins" fontSize="12px" fontWeight={400} color={themevals.colors.dkgrey}>{` · ${invNo}`}</Span>
                        </P>
                        {action ? (
                          <TextLink
                            onClick={() => {
                              // Both directions open the invoice's panel, which offers the
                              // provider's PO in the same step (2026-11-04).
                              navigate(`/invoices/${inv.id}?fuel=${action}`);
                            }}
                          >
                            {action === "remove" ? "Remove on invoice" : "Add to invoice"}
                          </TextLink>
                        ) : paid ? (
                          <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey}>Paid - change with a credit note in Zoho</Span>
                        ) : null}
                      </Flex>
                    );
                  })}
                </Box>
              );
            })()}
            {/* Ad-hoc charges (trip charge, site not ready, ...) on this job's
                client invoice(s), 2026-11-03. Lives here because this is where
                the operator is when the provider reports the extra trip. One
                action writes the client line and the provider's PO line. */}
            {isCompany && (() => {
              const entries = (projectTimeline ?? []).filter((e: any) => e?.invoice?.id && Number(e.invoice.status) !== 4);
              if (!entries.length) return null;
              return (
                <Box sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "16px 20px 20px 20px" }}>
                  <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Additional Charges</Label>
                  {entries.map((e: any) => {
                    const inv = e.invoice;
                    const invNo = `GMI-${String(inv.sequence ?? "").padStart(5, "0")}`;
                    const paid = (inv.payments ?? []).length > 0 || [2, 3].includes(Number(inv.status));
                    const lines = (inv.lineitems ?? []).filter((li: any) => li?.type === CHARGE_TYPE);
                    const poLines: any[] = (e.purchaseOrders ?? []).flatMap((po: any) => (Number(po?.status) === 6 ? [] : (po.lineitems ?? []).filter((li: any) => li?.type === CHARGE_TYPE).map((li: any) => ({ ...li, poSequence: po.sequence }))));
                    return (
                      <Box key={inv.id} sx={{ marginTop: "5px" }}>
                        {lines.length === 0 && (
                          <Flex sx={{ alignItems: "baseline", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"0px"} marginBottom={"0px"}>
                              None
                              <Span fontFamily="Poppins" fontSize="12px" fontWeight={400} color={themevals.colors.dkgrey}>{` · ${invNo}`}</Span>
                            </P>
                            {paid ? (
                              <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey}>Paid - bill extras on a new invoice</Span>
                            ) : (
                              <TextLink onClick={() => openChargeModal(inv, e.purchaseOrders ?? [])}>Add charge</TextLink>
                            )}
                          </Flex>
                        )}
                        {lines.map((li: any) => {
                          const twin = poLines.find((pl: any) => pl.entityId === li.entityId);
                          const client = Number(li.price ?? 0) * Number(li.quantity ?? 1);
                          const provider = twin ? Number(twin.cost ?? 0) * Number(twin.quantity ?? 1) : null;
                          return (
                            <Flex key={li.id} sx={{ alignItems: "baseline", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginTop: "3px" }}>
                              <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"0px"} marginBottom={"0px"}>
                                {li.name}: client {formatCurrency(client)}
                                <Span fontFamily="Poppins" fontSize="12px" fontWeight={400} color={themevals.colors.dkgrey}>
                                  {provider != null ? ` · provider ${formatCurrency(provider)} on GMO-${String(twin.poSequence ?? "").padStart(5, "0")}` : " · not on a PO"}{` · ${invNo}`}
                                </Span>
                              </P>
                              {!paid && (
                                <TextLink onClick={() => setRemoveChargeTarget({ invoice: inv, chargeId: li.entityId, name: li.name })}>Remove</TextLink>
                              )}
                            </Flex>
                          );
                        })}
                        {lines.length > 0 && !paid && (
                          <TextLink style={{ display: "inline-block", marginTop: "4px" }} onClick={() => openChargeModal(inv, e.purchaseOrders ?? [])}>Add another charge</TextLink>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Flex>
        </Flex>
      )}
      {/* Session 27 — site map card removed from Overview. Sidebar
          SiteMapSection (Projects/view.tsx) is the single canonical
          preview, matching Quote / Invoice / PO detail convention. */}
      {/* end client info */}
      <Flex sx={{ marginTop: "30px" }}>
        <Box>
          <P marginTop="0px" marginBottom="15px" fontFamily="Poppins" fontSize="14px" fontWeight="600" color={themevals.colors.dkgrey}>
            Items
          </P>
        </Box>
      </Flex>
      {/* begin items */}
      {projectTimeline && projectTimeline.length > 0 ? (
        projectTimeline.map(function (entry: any, index: number) {
          // Collect PO line items for cost (across all POs in this entry)
          const poLineitems: any[] = Array.isArray(entry.purchaseOrders) ? entry.purchaseOrders.flatMap((po: any) => (Array.isArray(po.lineitems) ? po.lineitems : [])) : [];

          // A provider no longer receives the client quote / invoice lines
          // (2026-10-20): its items come from its own PO lines.
          const lineitems =
            isProvider
              ? poLineitems
              : entry.invoice && Array.isArray(entry.invoice.lineitems) && entry.invoice.lineitems.length > 0
              ? entry.invoice.lineitems
              : entry.quote && Array.isArray(entry.quote.lineitems)
              ? entry.quote.lineitems
              : [];
          const hasPoCost = poLineitems.length > 0;

          const productItem = lineitems.find((item: any) => item.type === "product");
          const productName = productItem?.name || "-";
          const serviceItem = lineitems.find((item: any) => item.type === "service");
          const addonItems = lineitems.filter((item: any) => item.type === "addon-product");
          const serviceName = serviceItem?.name || "-";

          // Derive product cost per unit from PO when available, fall back to quote/invoice
          const productCostPerUnit = (() => {
            if (!hasPoCost) return productItem?.cost;
            // Prefer matching by entityId if available; otherwise first product line in POs
            const match = productItem?.entityId ? poLineitems.find((li: any) => li.type === "product" && li.entityId === productItem.entityId) : poLineitems.find((li: any) => li.type === "product");
            return typeof match?.cost === "number" ? match.cost : productItem?.cost;
          })();

          // Provider / depot of record come from the live PO, not the client
          // invoice line: a PO re-issued to another provider leaves the invoice's
          // product line pointing at the original depot (GMP 3e549c80, 2026-11-03).
          const livePOs: any[] = Array.isArray(entry.purchaseOrders) ? entry.purchaseOrders.filter((po: any) => po && po.status !== 6) : [];
          const primaryPO = livePOs.reduce((best: any, po: any) => (!best || (po.sequence ?? 0) > (best.sequence ?? 0) ? po : best), null);
          const poProductLine = primaryPO && Array.isArray(primaryPO.lineitems) ? primaryPO.lineitems.find((li: any) => li.type === "product") : null;
          const providerOfRecord = primaryPO?.provider?.name || poProductLine?.depot?.provider?.name || productItem?.depot?.provider?.name;
          const depotOfRecord = poProductLine?.depot?.name || (primaryPO && primaryPO.provider?.name !== productItem?.depot?.provider?.name ? undefined : productItem?.depot?.name);

          const productPricePerUnit = typeof productItem?.price === "number" ? productItem.price : undefined;
          const markupPercent =
            typeof productCostPerUnit === "number" && productCostPerUnit > 0 && typeof productPricePerUnit === "number"
              ? Math.round(((productPricePerUnit - productCostPerUnit) / productCostPerUnit) * 100)
              : 0;

          // The card is the product/service path: its totals are quantity x the
          // unit figures shown on it. Charges and fuel surcharge have their own
          // rows under Financial Info and are left out here, so the card adds up
          // (GMP 3e549c80, 2026-11-04: trip charge was inflating both totals).
          const isExtra = (li: any) => li?.type === "charge" || li?.type === "fuel_surcharge";
          // A provider's "Your Total Pay (all PO lines)" keeps every line - they are paid the charge too.
          const costItemsForTotals = (hasPoCost ? poLineitems : lineitems).filter((li: any) => isProvider || !isExtra(li));
          const priceItemsForTotals = lineitems.filter((li: any) => !isExtra(li));
          // Shown under each total so the card ties to the PO / invoice total.
          const extraCost = isProvider ? 0 : getQuoteCostTotal((hasPoCost ? poLineitems : lineitems).filter(isExtra));
          const extraPrice = getQuotePriceTotal(lineitems.filter(isExtra));
          const tieNote = (extra: number, base: number, doc: string) =>
            extra > 0 ? (
              <span style={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey, marginTop: 2 }}>
                {`+ ${formatCurrency(extra)} charges = ${formatCurrency(base + extra)} ${doc} total`}
              </span>
            ) : null;

          return (
            <Flex
              key={index}
              sx={{
                flex: 1,
                borderRadius: "6px",
                border: `1px solid ${themevals.colors.inputOutline}`,
                boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
                flexDirection: "column",
                marginBottom: "20px",
              }}
            >
              <Flex sx={{ padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, flexDirection: "row", alignItems: "center" }}>
                <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600" }}>{productName}</Flex>
                <Flex sx={{ marginLeft: "5px", marginRight: "5px", color: themevals.colors.dkgrey }}>-</Flex>
                <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "500", color: themevals.colors.dkgrey }}>{serviceName}</Flex>
              </Flex>
              <Grid gap={0} columns={[1, isCompany ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
                <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                  <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Quantity</Label>
                  <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                    {productItem?.quantity} {productItem?.unit}
                  </P>
                </Flex>
                {isCompany && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Markup</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {markupPercent}%
                    </P>
                  </Flex>
                )}
                {(isCompany || isProvider) && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    {/* A provider is paid this; "cost" is our word for it (2026-10-20). */}
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>{isProvider ? "Your Rate per Yard" : "Bulk Product Cost/Unit"}</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {isProvider && typeof productCostPerUnit === "number" ? formatCurrency(productCostPerUnit) : `$${productCostPerUnit}`}
                    </P>
                  </Flex>
                )}
                {!isProvider && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Bulk Product Price/Unit</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      ${productItem?.price}
                    </P>
                  </Flex>
                )}
                {!isCompany && !isProvider && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Grand Total Price</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {formatCurrency(getQuotePriceTotal(priceItemsForTotals))}
                    </P>
                  </Flex>
                )}
                {(isCompany || isProvider) && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>{isProvider ? "Your Total Pay (all PO lines)" : "Grand Total Cost"}</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {formatCurrency(getQuoteCostTotal(costItemsForTotals))}
                    </P>
                    {tieNote(extraCost, getQuoteCostTotal(costItemsForTotals), hasPoCost ? "PO" : "cost")}
                  </Flex>
                )}
                {isCompany && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Grand Total Price</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {formatCurrency(getQuotePriceTotal(priceItemsForTotals))}
                    </P>
                    {tieNote(extraPrice, getQuotePriceTotal(priceItemsForTotals), "invoice")}
                  </Flex>
                )}
                {isCompany && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Provider</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {providerOfRecord}
                    </P>
                  </Flex>
                )}
                {isCompany && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Depot</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {depotOfRecord || "—"}
                    </P>
                  </Flex>
                )}
              </Grid>
              {addonItems?.length > 0 && (
                <Flex sx={{ flexDirection: "column", padding: "0px 20px 20px 20px" }}>
                  <H5 fontFamily="Poppins" fontSize="12px" marginLeft="0px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                    Add-on Products
                  </H5>
                  <TableBasic data={addonItems} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} />
                </Flex>
              )}
            </Flex>
          );
        })
      ) : (
        <TimelineFallback status={timelineStatus} onRetry={_init} />
      )}
      {isProvider && (
        <>
          <Flex sx={{ marginTop: "30px" }}>
            <Box>
              <P marginTop="0px" marginBottom="15px" fontFamily="Poppins" fontSize="14px" fontWeight="600" color={themevals.colors.dkgrey}>
                Dispatches
              </P>
            </Box>
          </Flex>

          {providerPurchaseOrders.length === 0 ? (
            <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", marginBottom: "20px" }}>
              <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="14px" marginTop="0px" marginBottom="0px">
                There are no dispatchable purchase orders for this provider yet.
              </P>
            </Flex>
          ) : (
            providerPurchaseOrders.map((po: any) => {
              const dispatchList = dispatchesByPo[po.id] ?? [];
              const activeDispatch = dispatchList?.[0] ?? null;
              const poSequenceLabel = `GMO-${String(po.sequence ?? 0).padStart(5, "0")}`;
              const cardTitle = activeDispatch ? `Dispatch# GMD-${String(activeDispatch.sequence ?? 0).padStart(5, "0")}` : `No Dispatch created for PO# ${poSequenceLabel}`;

              return (
                <Flex
                  key={po.id}
                  sx={{
                    flex: 1,
                    borderRadius: "6px",
                    border: `1px solid ${themevals.colors.inputOutline}`,
                    boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
                    flexDirection: "column",
                    marginBottom: "20px",
                  }}
                >
                  <Flex sx={{ padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, flexDirection: "row", alignItems: "center" }}>
                    <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600" }}>{cardTitle}</Flex>
                    <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                      {!activeDispatch ? (
                        <BasicButton
                          type="button"
                          $submitting={dispatchSubmitting}
                          styles={{ margin: "0 0 0 10px" }}
                          $iconHeight="14px"
                          $iconWidth="14px"
                          $loadingColor="#fff"
                          keepText={true}
                          onClick={() => openCreateDispatchModal(po)}
                        >
                          {!dispatchSubmitting && <PlusIcon fill={"#fff"} />}
                          Add Dispatch
                        </BasicButton>
                      ) : (
                        <>
                          <BasicButton
                            type="button"
                            scheme="secondaryOutline"
                            $submitting={dispatchSubmitting}
                            styles={{ margin: "0 0 0 10px" }}
                            $iconHeight="14px"
                            $iconWidth="14px"
                            $loadingColor="#fff"
                            keepText={true}
                            onClick={() => openEditDispatchModal(po, activeDispatch)}
                          >
                            <PencilIcon fill={"#000"} />
                            Edit
                          </BasicButton>
                          {activeDispatch.status === 0 || activeDispatch.status === 3 ? (
                            <BasicButton
                              type="button"
                              $submitting={dispatchSubmitting}
                              styles={{ margin: "0 0 0 20px" }}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#fff"
                              keepText={true}
                              onClick={() => markOnTheWay(activeDispatch.id, po.id)}
                            >
                              {!dispatchSubmitting && <CheckMarkIcon fill={"#fff"} />}
                              On our way
                            </BasicButton>
                          ) : (
                              <BasicButton type="button" styles={{ margin: "0 0 0 20px" }} $iconHeight="14px" $iconWidth="14px" keepText={true} onClick={() => copyDispatchLink(activeDispatch.id, po.id)}>
                              <CheckMarkIcon fill={themevals.colors.white} />
                              Copy dispatch link
                            </BasicButton>
                          )}
                        </>
                      )}
                    </Flex>
                  </Flex>
                  <Flex sx={{ padding: "20px", flexDirection: "column" }}>
                    {!activeDispatch && (
                      <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="14px">
                        Click “Add Dispatch” above to generate a Dispatch Link and let us know you are on your way.
                      </P>
                    )}
                    {activeDispatch && (
                      <>
                        <Grid gap={0} columns={"1fr 1fr 1fr"} sx={{ flexDirection: "column" }}>
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {(() => {
                                switch (activeDispatch?.status) {
                                  case 0:
                                    return (
                                      <Badge as="span" variant="warning">
                                        Created
                                      </Badge>
                                    );
                                  case 1:
                                    return (
                                      <Badge as="span" variant="success">
                                        Dispatched
                                      </Badge>
                                    );
                                  case 3:
                                    return (
                                      <Badge as="span" variant="info">
                                        On Hold
                                      </Badge>
                                    );
                                  case 4:
                                    return (
                                      <Badge as="span" variant="info">
                                        On Site
                                      </Badge>
                                    );
                                  case 5:
                                    return (
                                      <Badge as="span" variant="success">
                                        Complete
                                      </Badge>
                                    );
                                  default:
                                    return (
                                      <Badge as="span" variant="warning">
                                        Created
                                      </Badge>
                                    );
                                }
                              })()}
                            </P>
                          </Flex>
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Truck IDs</Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {(activeDispatch.truckIds || []).length > 0 ? activeDispatch.truckIds.join(", ") : "—"}
                            </P>
                          </Flex>
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Dispatch Date</Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {activeDispatch.dispatchDate ? moment(activeDispatch.dispatchDate).format("MMM D, YYYY h:mm A") : "-"}
                            </P>
                          </Flex>
                        </Grid>
                        <Grid gap={0} columns={"1fr 1fr 1fr"} sx={{ flexDirection: "column", marginTop: "30px" }}>
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px", minWidth: 0, paddingRight: "16px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Dispatch Link</Label>
                            {/* The full URL ran into the PIN column (2026-10-20): show a short link + Copy. */}
                            <Flex sx={{ alignItems: "center", gap: "10px", marginTop: "5px", flexWrap: "wrap" }}>
                            <Link
                              href={`${window.location.origin}/dispatches/${activeDispatch?.id ?? ""}`}
                              target="_blank"
                              title={`${window.location.origin}/dispatches/${activeDispatch?.id ?? ""}`}
                              sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, textDecoration: "underline" }}
                              onClick={() => {
                                if (!activeDispatch?.id) return;
                                void trackActivity({
                                  module: "projects",
                                  action: "click_dispatch_link",
                                  actionType: "click",
                                  userId: user?.id || null,
                                  companyId: user?.company?.id || project?.company?.id || null,
                                  entityType: "dispatch",
                                  entityId: activeDispatch.id,
                                  metadata: {
                                    projectId: project?.id || null,
                                    purchaseOrderId: po.id,
                                  },
                                  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                                });
                              }}
                            >
                              Open driver page ↗
                            </Link>
                            <Box
                              as="button"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(`${window.location.origin}/dispatches/${activeDispatch?.id ?? ""}`);
                                  toast.add("Dispatch link copied.", "var(--theme-ui-colors-success)");
                                } catch {
                                  toast.add("Could not copy the link.", "var(--theme-ui-colors-danger)");
                                }
                              }}
                              sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "4px", background: "white", padding: "2px 10px", fontFamily: "Poppins", fontSize: "12px", cursor: "pointer" }}
                            >
                              Copy link
                            </Box>
                            </Flex>
                          </Flex>
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Dispatch security pin (PO#)</Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {activeDispatch.purchaseOrder?.sequence != null
                                ? String(activeDispatch.purchaseOrder.sequence).padStart(5, "0")
                                : "-"}
                            </P>
                          </Flex>
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Completion Date</Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {activeDispatch.endDate ? moment(activeDispatch.endDate).format("MMM D, YYYY h:mm A") : "-"}
                            </P>
                          </Flex>
                        </Grid>
                      </>
                    )}
                  </Flex>
                </Flex>
              );
            })
          )}
        </>
      )}

      {/* start timeline */}
      {!isProvider && (
        <>
          <Flex sx={{ marginTop: "30px" }}>
            <Box>
              <P marginTop="0px" marginBottom="15px" fontFamily="Poppins" fontSize="14px" fontWeight="600" color={themevals.colors.dkgrey}>
                Timeline
              </P>
            </Box>
          </Flex>

          {projectTimeline && projectTimeline.length > 0 ? (
            <Box sx={{ marginBottom: "60px", overflowX: ["auto", "visible"] }}>
              {/* First (origin) quote row */}
              <Grid
                columns={isCompany ? "1fr 40px 1fr 40px 1fr 40px 1fr" : "1fr 40px 1fr 40px 1fr"}
                gap={0}
                sx={{
                  marginBottom: "20px",
                  alignItems: "stretch",
                  minWidth: [isCompany ? "650px" : "480px", "auto"],
                }}
              >
                <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                  <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                    <Flex sx={{ flex: 1, alignItems: "center" }}>
                      <Flex>
                        <RFQIcon width="18px" fill={themevals.colors.dkgrey} />
                      </Flex>
                      <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>RFQ</Box>
                    </Flex>
                    <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                      <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("rfq", projectTimeline[0].quoteRequest.id)}>
                        #{`GMR-${String(projectTimeline[0].quoteRequest.sequence).padStart(5, "0")}`}
                      </Box>
                    </Flex>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                    <Box>
                      <Badge as="span" variant="success">
                        Quoted
                      </Badge>
                    </Box>
                    <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Quoted On</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {moment(projectTimeline[0].quote.createdAt).format("MMM D, YYYY")}
                      </P>
                    </Flex>
                  </Flex>
                </Flex>
                <Flex>
                  <LongArrowIcon width="40px" fill={themevals.colors.mdgrey} />
                </Flex>
                <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                  <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                    <Flex sx={{ flex: 1, alignItems: "center" }}>
                      <Flex>
                        <QuotesIcon width="18px" fill={themevals.colors.dkgrey} />
                      </Flex>
                      <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>Quote</Box>
                    </Flex>
                    <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                      <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("quote", projectTimeline[0].quote.id)}>
                        #{`GMQ-${String(projectTimeline[0].quote.sequence).padStart(5, "0")}`}
                      </Box>
                    </Flex>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                    <Box>
                      {(() => {
                        switch (projectTimeline[0].quote?.status) {
                          case 0:
                            return (
                              <Badge as="span" variant="warning">
                                New
                              </Badge>
                            );
                          case 1:
                            return (
                              <Badge as="span" variant="success">
                                Accepted
                              </Badge>
                            );
                          case 2:
                            return (
                              <Badge as="span" variant="danger">
                                Denied
                              </Badge>
                            );
                          default:
                            return (
                              <Badge as="span" variant="warning">
                                New
                              </Badge>
                            );
                        }
                      })()}
                    </Box>
                    <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                        {(() => {
                          switch (projectTimeline[0].quote?.status) {
                            case 0:
                              return "Created On";
                            case 1:
                              return "Accepted On";
                            case 2:
                              return "Denied On";
                            default:
                              return "Created On";
                          }
                        })()}
                      </Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {projectTimeline[0].quote.acceptedDate ? moment(projectTimeline[0].quote.acceptedDate).format("MMM D, YYYY") : "-"}
                      </P>
                    </Flex>
                  </Flex>
                </Flex>
                {isCompany && projectTimeline[0].purchaseOrders && projectTimeline[0].purchaseOrders.length > 0 && (
                  <>
                    <Flex>
                      <LongArrowIcon width="40px" fill={themevals.colors.mdgrey} />
                    </Flex>
                    <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                      <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                        <Flex sx={{ flex: 1, alignItems: "center" }}>
                          <Flex>
                            <PurchaseOrderIcon width="18px" fill={themevals.colors.dkgrey} />
                          </Flex>
                          <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>PO</Box>
                        </Flex>
                        <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                          <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("po", projectTimeline[0].purchaseOrders[0].id)}>
                            #{`GMO-${String(projectTimeline[0].purchaseOrders[0].sequence).padStart(5, "0")}`}
                          </Box>
                        </Flex>
                      </Flex>
                      <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                        <Flex>
                          <Box sx={{ flex: 1 }}>
                            {(() => {
                              switch (projectTimeline[0].purchaseOrders[0]?.status) {
                                case 0:
                                  return (
                                    <Badge as="span" variant="warning">
                                      New
                                    </Badge>
                                  );
                                case 1:
                                  return (
                                    <Badge as="span" variant="info">
                                      Sent
                                    </Badge>
                                  );
                                case 2:
                                  return (
                                    <Badge as="span" variant="success">
                                      Accepted
                                    </Badge>
                                  );
                                case 3:
                                  return (
                                    <Badge as="span" variant="warning">
                                      Billed
                                    </Badge>
                                  );
                                case 4:
                                  return (
                                    <Badge as="span" variant="danger">
                                      Denied
                                    </Badge>
                                  );
                                case 5:
                                  return (
                                    <Badge as="span" variant="danger">
                                      Closed
                                    </Badge>
                                  );
                                default:
                                  return (
                                    <Badge as="span" variant="warning">
                                      New
                                    </Badge>
                                  );
                              }
                            })()}
                          </Box>
                          {projectTimeline[0].purchaseOrders[0]?.status === 4 && (
                            <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                              <BasicButton scheme="secondaryOutline" onClick={() => navigate(`/purchase-orders/${projectTimeline[0].purchaseOrders[0].id}/replace`)}>
                                <ReloadIcon fill={"#000"} />
                                Replace
                              </BasicButton>
                            </Flex>
                          )}
                        </Flex>
                        <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                            {(() => {
                              switch (projectTimeline[0].purchaseOrders[0]?.status) {
                                case 0:
                                  return "Created On";
                                case 2:
                                  return "Accepted On";
                                case 3:
                                  return "Due On";
                                case 4:
                                  return "Denied On";
                                default:
                                  return "Created On";
                              }
                            })()}
                          </Label>
                          <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {getPoDisplayDate(projectTimeline[0].purchaseOrders[0])}
                          </P>
                        </Flex>
                        {(() => {
                          const poId = projectTimeline[0].purchaseOrders[0]?.id;
                          const invStatus = poId && invoiceStatusByPo[poId];
                          if (!invStatus) return null;
                          return (
                            <Flex
                              sx={{ flexDirection: "column", marginTop: "12px", cursor: "pointer" }}
                              onClick={() => navigate(`/purchase-orders/${poId}?tab=invoices`)}
                            >
                              <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Provider Invoice</Label>
                              <Box sx={{ marginTop: "5px" }}>
                                {invStatus.status === "pending" && (
                                  <Badge as="span" variant="warning">Pending Review</Badge>
                                )}
                                {invStatus.status === "approved" && (
                                  <Badge as="span" variant="success">Approved</Badge>
                                )}
                                {invStatus.status === "rejected" && (
                                  <Badge as="span" variant="danger">Rejected</Badge>
                                )}
                              </Box>
                            </Flex>
                          );
                        })()}
                      </Flex>
                    </Flex>
                  </>
                )}
                <Flex>
                  <LongArrowIcon width="40px" fill={themevals.colors.mdgrey} />
                </Flex>
                <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                  <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                    <Flex sx={{ flex: 1, alignItems: "center" }}>
                      <Flex>
                        <InvoicesIcon width="18px" fill={themevals.colors.dkgrey} />
                      </Flex>
                      <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>Invoice</Box>
                    </Flex>
                    {projectTimeline[0].invoice && (
                      <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                        <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("invoice", projectTimeline[0].invoice.id)}>
                          #{`GMI-${String(projectTimeline[0].invoice.sequence).padStart(5, "0")}`}
                        </Box>
                      </Flex>
                    )}
                  </Flex>
                  {!projectTimeline[0].invoice ? (
                    <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                      {isCompany ? (
                        <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${project.id}/add-invoice/${projectTimeline[0].quote.id}`)}>
                          <PlusCircleIcon width="26px" height="26px" fill={themevals.colors.black} />
                        </Box>
                      ) : (
                        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>There is no invoice on this project.</Box>
                      )}
                    </Flex>
                  ) : (
                    <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                      <Box>
                        <InvoiceStatusBadge invoice={projectTimeline[0].invoice} projectStatusName={project?.status?.name} />
                      </Box>
                      <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                          {(() => {
                            switch (projectTimeline[0].invoice?.status) {
                              case 0:
                                return "Created On";
                              case 1:
                                return "Sent On";
                              case 2:
                                return "Paid On";
                              case 3:
                                return "Due Date";
                              case 4:
                                return "Voided On";
                              default:
                                return "Created On";
                            }
                          })()}
                        </Label>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {(() => {
                            switch (projectTimeline[0].invoice?.status) {
                              case 0:
                                return moment(projectTimeline[0].invoice.createdAt).format("MMM D, YYYY");
                              case 1:
                                return moment(projectTimeline[0].invoice.sentDate).format("MMM D, YYYY");
                              case 2: {
                                const payments = projectTimeline[0].invoice?.payments || [];
                                const mostRecent = payments.reduce((latest: Payment, payment: Payment) => {
                                  const updatedAt = new Date(payment.updatedAt);
                                  return updatedAt > new Date(latest.updatedAt) ? payment : latest;
                                }, payments[0]);
                                return mostRecent ? moment(mostRecent.updatedAt).format("MMM D, YYYY") : "-";
                              }
                              case 3:
                                return projectTimeline[0].invoice.dueDate ? moment(projectTimeline[0].invoice.dueDate).format("MMM D, YYYY") : "-";
                              case 4:
                                return moment(projectTimeline[0].invoice.voidedDate).format("MMM D, YYYY");
                              default:
                                return moment(projectTimeline[0].invoice.createdAt).format("MMM D, YYYY");
                            }
                          })()}
                        </P>
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </Grid>

              {/* Other quote/quoteRequest rows */}

              {projectTimeline.slice(1).map((entry: any, idx: number) => {
                const hasPurchaseOrders = Array.isArray(entry?.purchaseOrders) && entry.purchaseOrders.length > 0;
                const shouldShowPurchaseOrder = isCompany && hasPurchaseOrders;
                const primaryPurchaseOrder = shouldShowPurchaseOrder ? entry.purchaseOrders[0] : null;
                return (
                  <Grid
                    columns={isCompany ? "1fr 40px 1fr 40px 1fr 40px 1fr" : "1fr 40px 1fr 40px 1fr"}
                    gap={0}
                    key={entry?.id ?? entry?.quote?.id ?? entry?.quoteRequest?.id ?? idx}
                    sx={{
                      marginBottom: "20px",
                      marginTop: "20px",
                      alignItems: "stretch",
                      minWidth: [isCompany ? "650px" : "480px", "auto"],
                    }}
                  >
                    <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                      <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                        <Flex sx={{ flex: 1, alignItems: "center" }}>
                          <Flex>
                            <RFQIcon width="18px" fill={themevals.colors.dkgrey} />
                          </Flex>
                          <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>RFQ</Box>
                        </Flex>
                        <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                          <Box sx={{ textDecoration: "underline", cursor: "pointer" }}>#{`GMR-${String(entry.quoteRequest.sequence).padStart(5, "0")}`}</Box>
                        </Flex>
                      </Flex>
                      <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                        <Box>
                          <Badge as="span" variant="success">
                            Quoted
                          </Badge>
                        </Box>
                        <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Quoted On</Label>
                          <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {moment(entry.quote.createdAt).format("MMM D, YYYY")}
                          </P>
                        </Flex>
                      </Flex>
                    </Flex>
                    <Flex>
                      <LongArrowIcon width="40px" fill={themevals.colors.mdgrey} />
                    </Flex>
                    <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                      <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                        <Flex sx={{ flex: 1, alignItems: "center" }}>
                          <Flex>
                            <QuotesIcon width="18px" fill={themevals.colors.dkgrey} />
                          </Flex>
                          <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>Quote</Box>
                        </Flex>
                        <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                          <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("quote", entry.quote.id)}>
                            #{`GMQ-${String(entry.quote.sequence).padStart(5, "0")}`}
                          </Box>
                        </Flex>
                      </Flex>
                      <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                        <Box>
                          {(() => {
                            switch (entry.quote?.status) {
                              case 0:
                                return (
                                  <Badge as="span" variant="warning">
                                    New
                                  </Badge>
                                );
                              case 1:
                                return (
                                  <Badge as="span" variant="success">
                                    Accepted
                                  </Badge>
                                );
                              case 2:
                                return (
                                  <Badge as="span" variant="danger">
                                    Denied
                                  </Badge>
                                );
                              default:
                                return (
                                  <Badge as="span" variant="warning">
                                    New
                                  </Badge>
                                );
                            }
                          })()}
                        </Box>
                        <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                            {(() => {
                              switch (entry.quote?.status) {
                                case 0:
                                  return "Created On";
                                case 1:
                                  return "Accepted On";
                                case 2:
                                  return "Denied On";
                                default:
                                  return "Created On";
                              }
                            })()}
                          </Label>
                          <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {(() => {
                              switch (entry.quote?.status) {
                                case 0:
                                  return moment(entry.quote.createdAt).format("MMM D, YYYY");
                                case 1:
                                  return moment(entry.quote.acceptedDate).format("MMM D, YYYY");
                                case 2:
                                  return moment(entry.invoice.deniedDate).format("MMM D, YYYY");
                                default:
                                  return moment(entry.quote.createdAt).format("MMM D, YYYY");
                              }
                            })()}
                          </P>
                        </Flex>
                      </Flex>
                    </Flex>
                    {shouldShowPurchaseOrder && primaryPurchaseOrder && (
                      <>
                        <Flex>
                          <LongArrowIcon width="40px" fill={themevals.colors.mdgrey} />
                        </Flex>
                        <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                          <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                            <Flex sx={{ flex: 1, alignItems: "center" }}>
                              <Flex>
                                <PurchaseOrderIcon width="18px" fill={themevals.colors.dkgrey} />
                              </Flex>
                              <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>PO</Box>
                            </Flex>
                            <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                              <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("po", primaryPurchaseOrder.id)}>
                                #{`GMO-${String(primaryPurchaseOrder.sequence).padStart(5, "0")}`}
                              </Box>
                            </Flex>
                          </Flex>
                          <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                            <Flex>
                              <Box sx={{ flex: 1 }}>
                                {(() => {
                                  switch (primaryPurchaseOrder?.status) {
                                    case 0:
                                      return (
                                        <Badge as="span" variant="warning">
                                          New
                                        </Badge>
                                      );
                                    case 1:
                                      return (
                                        <Badge as="span" variant="info">
                                          Sent
                                        </Badge>
                                      );
                                    case 2:
                                      return (
                                        <Badge as="span" variant="success">
                                          Accepted
                                        </Badge>
                                      );
                                    case 3:
                                      return (
                                        <Badge as="span" variant="warning">
                                          Billed
                                        </Badge>
                                      );
                                    case 4:
                                      return (
                                        <Badge as="span" variant="danger">
                                          Denied
                                        </Badge>
                                      );
                                    case 5:
                                      return (
                                        <Badge as="span" variant="danger">
                                          Closed
                                        </Badge>
                                      );
                                    default:
                                      return (
                                        <Badge as="span" variant="warning">
                                          New
                                        </Badge>
                                      );
                                  }
                                })()}
                              </Box>
                              {primaryPurchaseOrder?.status === 4 && (
                                <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                                  <BasicButton scheme="secondaryOutline" onClick={() => navigate(`/purchase-orders/${primaryPurchaseOrder.id}/replace`)}>
                                    <ReloadIcon fill={"#000"} />
                                    Replace
                                  </BasicButton>
                                </Flex>
                              )}
                            </Flex>
                            <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                              <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                                {(() => {
                                  switch (primaryPurchaseOrder?.status) {
                                    case 0:
                                      return "Created On";
                                    case 2:
                                      return "Accepted On";
                                    case 3:
                                      return "Due On";
                                    case 4:
                                      return "Denied On";
                                    default:
                                      return "Created On";
                                  }
                                })()}
                              </Label>
                              <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                                {getPoDisplayDate(primaryPurchaseOrder)}
                              </P>
                            </Flex>
                            {(() => {
                              const poId = primaryPurchaseOrder?.id;
                              const invStatus = poId && invoiceStatusByPo[poId];
                              if (!invStatus) return null;
                              return (
                                <Flex
                                  sx={{ flexDirection: "column", marginTop: "12px", cursor: "pointer" }}
                                  onClick={() => navigate(`/purchase-orders/${poId}?tab=invoices`)}
                                >
                                  <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Provider Invoice</Label>
                                  <Box sx={{ marginTop: "5px" }}>
                                    {invStatus.status === "pending" && (
                                      <Badge as="span" variant="warning">Pending Review</Badge>
                                    )}
                                    {invStatus.status === "approved" && (
                                      <Badge as="span" variant="success">Approved</Badge>
                                    )}
                                    {invStatus.status === "rejected" && (
                                      <Badge as="span" variant="danger">Rejected</Badge>
                                    )}
                                  </Box>
                                </Flex>
                              );
                            })()}
                          </Flex>
                        </Flex>
                      </>
                    )}
                    <Flex>
                      <LongArrowIcon width="40px" fill={themevals.colors.mdgrey} />
                    </Flex>
                    <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", flexDirection: "column" }}>
                      <Flex sx={{ flexDirection: "row", alignItems: "center", padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                        <Flex sx={{ flex: 1, alignItems: "center" }}>
                          <Flex>
                            <InvoicesIcon width="18px" fill={themevals.colors.dkgrey} />
                          </Flex>
                          <Box sx={{ fontWeight: 600, marginLeft: "8px", fontSize: "12px" }}>Invoice</Box>
                          {entry.invoice && (
                            <Flex sx={{ flex: 1, justifyContent: "flex-end", fontSize: "12px", fontFamily: "Poppins", fontWeight: 600 }}>
                              <Box sx={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openAssetTab("invoice", entry.invoice.id)}>
                                #{`GMI-${String(entry.invoice.sequence).padStart(5, "0")}`}
                              </Box>
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                      {!entry.invoice ? (
                        entry.quote.status === 1 && isCompany ? (
                          <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                            <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${project.id}/add-invoice/${entry.quote.id}`)}>
                              <PlusCircleIcon width="26px" height="26px" fill={themevals.colors.black} />
                            </Box>
                          </Flex>
                        ) : !isCompany ? (
                          <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, padding: "20px" }}>There is no invoice created for the corresponding quote.</Box>
                          </Flex>
                        ) : (
                          <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, padding: "20px" }}>
                              You must accept the corresponding quote before an invoice can be created.
                            </Box>
                          </Flex>
                        )
                      ) : (
                        <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                          <Box>
                            <InvoiceStatusBadge invoice={entry.invoice} projectStatusName={project?.status?.name} />
                          </Box>
                          <Flex sx={{ flexDirection: "column", marginBottom: "0px", marginTop: "15px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                              {(() => {
                                switch (entry.invoice?.status) {
                                  case 0:
                                    return "Created On";
                                  case 1:
                                    return "Sent On";
                                  case 2:
                                    return "Paid On";
                                  case 3:
                                    return "Due Date";
                                  case 4:
                                    return "Voided On";
                                  default:
                                    return "Created On";
                                }
                              })()}
                            </Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {(() => {
                                switch (entry.invoice?.status) {
                                  case 0:
                                    return moment(entry.invoice.createdAt).format("MMM D, YYYY");
                                  case 1:
                                    return moment(entry.invoice.sentDate).format("MMM D, YYYY");
                                  case 2: {
                                    const payments = entry.invoice?.payments || [];
                                    const mostRecent = payments.reduce((latest: Payment, payment: Payment) => {
                                      const updatedAt = new Date(payment.updatedAt);
                                      return updatedAt > new Date(latest.updatedAt) ? payment : latest;
                                    }, payments[0]);
                                    return mostRecent ? moment(mostRecent.updatedAt).format("MMM D, YYYY") : "-";
                                  }
                                  case 3:
                                    return entry.invoice.dueDate ? moment(entry.invoice.dueDate).format("MMM D, YYYY") : "-";
                                  case 4:
                                    return moment(entry.invoice.voidedDate).format("MMM D, YYYY");
                                  default:
                                    return moment(entry.invoice.createdAt).format("MMM D, YYYY");
                                }
                              })()}
                            </P>
                          </Flex>
                        </Flex>
                      )}
                    </Flex>
                  </Grid>
                );
              })}
              <Grid
                columns={isCompany ? "1fr 40px 1fr 40px 1fr 40px 1fr" : "1fr 40px 1fr 40px 1fr"}
                gap={0}
                sx={{
                  marginBottom: "20px",
                  alignItems: "stretch",
                  minWidth: [isCompany ? "650px" : "480px", "auto"],
                }}
              >
                <Flex></Flex>
                <Flex></Flex>
                <Flex sx={{ alignItems: "center", justifyContent: "center" }}>
                  {isCompany && (
                    <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${project.id}/add-quote`)}>
                      <PlusCircleIcon width="26px" height="26px" fill={themevals.colors.black} />
                    </Box>
                  )}
                </Flex>
                <Flex></Flex>
                <Flex></Flex>
              </Grid>
            </Box>
          ) : (
            <TimelineFallback status={timelineStatus} onRetry={_init} />
          )}
        </>
      )}

      {confirmStepModalOpen && (
        <BasicModal
          title={`${selectedStep?.transition.actionText}?`}
          cancel_click={() => cancelStepModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => submitStepAction(selectedStep)}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <CheckMarkIcon fill={"#fff"} />}
              {selectedStep?.transition.actionText}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to change the status of the project?</P>
        </BasicModal>
      )}

      <ChargeAddModal invoice={chargeModal?.invoice ?? null} pos={chargeModal?.pos ?? []} onClose={() => setChargeModal(null)} onDone={() => _init()} />
      <ChargeRemoveModal target={removeChargeTarget} onClose={() => setRemoveChargeTarget(null)} onDone={() => _init()} />
      <Modal isOpen={createDispatchOpen} toggle={closeCreateDispatchModal} centered size={"sm"} closeOnBackdropClick={false}>
        <Formik initialValues={dispatchInitialValues} onSubmit={(values) => submitDispatchCreate(values)} enableReinitialize>
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            values: any;
            submitCount: number;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={closeCreateDispatchModal}>
                  <ModalIcon>
                    <ProvidersIcon fill={"#000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Create dispatch</ModalTitle>
                    <ModalSubtitle>{"Create a dispatch for the project."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  {selectedPurchaseOrder && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Purchase Order</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        #{`GMO-${String(selectedPurchaseOrder.sequence ?? 0).padStart(5, "0")}`}
                      </P>
                    </Flex>
                  )}
                  <MultiText
                    name="truckIds"
                    label="Truck IDs"
                    value={values.truckIds}
                    onChange={(val: string[]) => setFieldValue("truckIds", val)}
                    $errors={errors.truckIds && submitCount > 0 ? errors.truckIds : null}
                    $customStyles={{ width: "100%", marginBottom: "30px" }}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondaryOutline" type="button" onClick={closeCreateDispatchModal} style={{ marginRight: "10px" }}>
                    Cancel
                  </Button>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                    Save dispatch
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={editDispatchOpen} toggle={closeEditDispatchModal} centered size={"sm"} closeOnBackdropClick={false}>
        <Formik initialValues={dispatchInitialValues} onSubmit={(values) => submitDispatchEdit(values)} enableReinitialize>
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            values: any;
            submitCount: number;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={closeEditDispatchModal}>
                  <ModalIcon>
                    <ProvidersIcon fill={"#000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Edit dispatch</ModalTitle>
                    <ModalSubtitle>{"Edit a dispatch for the project."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  {selectedPurchaseOrder && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Purchase Order</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        #{`GMO-${String(selectedPurchaseOrder.sequence ?? 0).padStart(5, "0")}`}
                      </P>
                    </Flex>
                  )}
                  <MultiText
                    name="truckIds"
                    label="Truck IDs"
                    value={values.truckIds}
                    onChange={(val: string[]) => setFieldValue("truckIds", val)}
                    $errors={errors.truckIds && submitCount > 0 ? errors.truckIds : null}
                    $customStyles={{ width: "100%", marginBottom: "30px" }}
                  />
                  {selectedDispatch && selectedDispatch.status === 1 && (
                    <Checkbox
                      label="Place on hold"
                      checked={values.placeOnHold}
                      onChange={() => setFieldValue("placeOnHold", !values.placeOnHold)}
                      labelSize="14px"
                      $customStyles={{ paddingBottom: "20px" }}
                    />
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondaryOutline" type="button" onClick={closeEditDispatchModal} style={{ marginRight: "10px" }}>
                    Cancel
                  </Button>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                    Save dispatch
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default Overview;
